import { useEffect, useState } from 'react';
import { ContextStrip } from './components/ContextStrip.tsx';
import { HistoryDrawer } from './components/HistoryDrawer.tsx';
import { TopBar } from './components/TopBar.tsx';
import { useSessions } from './hooks/useSessions.ts';
import { ACTIVE_REGULATION } from './mock/data.ts';
import { Account } from './pages/Account.tsx';
import { Community } from './pages/Community.tsx';
import { CommunityTeam } from './pages/CommunityTeam.tsx';
import { LiveChat } from './pages/LiveChat.tsx';
import { MetaDashboard } from './pages/MetaDashboard.tsx';
import { TeamBuild } from './pages/TeamBuild.tsx';

type Route = 'home' | 'chat' | 'build' | 'account' | 'community' | 'community-team';

function routeFromPath(): { route: Route; sessionId?: string; teamId?: number } {
  const p = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (p.startsWith('/account')) return { route: 'account' };
  if (p.startsWith('/build')) return { route: 'build' };
  if (p.startsWith('/community/')) {
    const id = Number.parseInt(p.slice('/community/'.length), 10);
    if (Number.isFinite(id) && id > 0) return { route: 'community-team', teamId: id };
    return { route: 'community' };
  }
  if (p.startsWith('/community')) return { route: 'community' };
  if (p.startsWith('/c/')) {
    const id = p.slice(3);
    return { route: 'chat', sessionId: id };
  }
  if (p === '/chat') return { route: 'chat' };
  // /meta merged into home — keep the path alive for any bookmarks but
  // render the same dashboard surface.
  return { route: 'home' };
}

export function App() {
  const [{ route, sessionId, teamId }, setRouteState] = useState(routeFromPath);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const { sessions, refresh } = useSessions();

  useEffect(() => {
    const onPop = () => setRouteState(routeFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    fetch('/api/auth/me')
      .then((r) => r.json())
      .then((j) => setSignedIn(j?.user != null))
      .catch(() => setSignedIn(false));
  }, []);

  // Refresh the history drawer's session list whenever it opens — picks up
  // freshly created sessions without polling.
  useEffect(() => {
    if (historyOpen) void refresh();
  }, [historyOpen, refresh]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-0)' }}>
      <TopBar onToggleHistory={() => setHistoryOpen((v) => !v)} signedIn={signedIn} />
      <ContextStrip regulation={ACTIVE_REGULATION} />
      <main>
        {route === 'home' && <MetaDashboard />}
        {route === 'chat' && <LiveChat initialSessionId={sessionId ?? null} />}
        {route === 'build' && <TeamBuild />}
        {route === 'account' && <Account />}
        {route === 'community' && <Community />}
        {route === 'community-team' && teamId != null && <CommunityTeam teamId={teamId} />}
      </main>
      <HistoryDrawer
        open={historyOpen}
        sessions={sessions}
        onClose={() => setHistoryOpen(false)}
        onSelect={(id) => {
          window.history.pushState({}, '', `/c/${id}`);
          setRouteState({ route: 'chat', sessionId: id });
          setHistoryOpen(false);
        }}
      />
    </div>
  );
}
